import React from 'react';
import { Flex, xcss, Stack, Inline, Box } from '@atlaskit/primitives';
import { JiraServiceManagementIcon } from '@atlaskit/temp-nav-app-icons/jira-service-management';
import Heading from '@atlaskit/heading';

import { Section } from '@atlaskit/menu';
import Skeleton from '@atlaskit/skeleton';
import { PremiumLandingPageEntryPointButtonWithDrawer } from '@atlassian/jira-servicedesk-premium-landing-page-buttons/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { PremiumLandingPageOrigin } from '@atlassian/jira-servicedesk-premium-landing-page/src/ui/types.tsx';
import { UpsellButton } from '@atlassian/jira-servicedesk-premium-landing-page/src/ui/upsell-button/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useDisplayGpEndDate } from '@atlassian/jira-servicedesk-premium-landing-page/src/services/use-display-gp-end-date/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { DirectBuyButton } from '@atlassian/jira-direct-buy-buttons/src/ui/index.tsx';
import { messages } from './messages.tsx';

type PremiumLandingPageTriggerBannerProps = {
	origin: PremiumLandingPageOrigin;
	testId?: string;
};

export const PremiumLandingPageTriggerBanner = ({
	origin,
	testId,
}: PremiumLandingPageTriggerBannerProps) => {
	const { formatMessage } = useIntl();
	const { gpEndDate, loading } = useDisplayGpEndDate();

	let subtitleLabel;
	if (loading) {
		subtitleLabel = formatMessage(messages.subtitleLabelLoading, {
			loadingSkeleton: (
				<Box
					xcss={subtitleSkeletonStyle}
					testId="servicedesk-premium-landing-page-trigger-banner.ui.premium-landing-page-trigger-banner-title-skeleton"
				>
					<Skeleton width="120px" height="16px" />
				</Box>
			),
		});
	} else if (gpEndDate) {
		subtitleLabel = formatMessage(messages.subtitleLabelGpEndDate, { gpEndDate });
	} else {
		// Also catches the case when the gpEndDate fetch had an error
		subtitleLabel = formatMessage(messages.subtitleLabelDefault);
	}

	return (
		<JSErrorBoundary
			id="jsmPremiumLandingPageTriggerBanner"
			packageName="jiraServiceDeskPremiumLandingPageTriggerBanner"
			fallback="flag"
		>
			<Section
				hasSeparator
				data-testid={
					testId ||
					'servicedesk-premium-landing-page-trigger-banner.ui.premium-landing-page-trigger-banner'
				}
			>
				<Flex xcss={containerStyles}>
					<Inline space="space.100" alignBlock="start" alignInline="start">
						<JiraServiceManagementIcon />
						<Stack space="space.075" xcss={contentWrapperStyles}>
							<Box xcss={headingStyles}>
								<Heading as="h2" size="xsmall">
									{subtitleLabel}
								</Heading>
							</Box>
							<Flex justifyContent="start" gap="space.100" xcss={buttonsWrapperStyles}>
								{fg('jsm_gp_announcements_banner') ? (
									<DirectBuyButton
										analyticsOrigin={origin}
										testId="servicedesk-premium-landing-page-trigger-banner.ui.direct-buy-button"
									/>
								) : (
									<UpsellButton
										analyticsOrigin={origin}
										buttonLabel={messages.tryItFreeButtonLabel}
									/>
								)}
								<PremiumLandingPageEntryPointButtonWithDrawer origin={origin} />
							</Flex>
						</Stack>
					</Inline>
				</Flex>
			</Section>
		</JSErrorBoundary>
	);
};

const subtitleSkeletonStyle = xcss({
	display: 'inline-block',
	verticalAlign: 'middle',
});

const containerStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	padding: 'space.200',
	maxWidth: '496px',
});

const contentWrapperStyles = xcss({
	width: '100%',
	alignItems: 'flex-start',
});

const buttonsWrapperStyles = xcss({
	width: '100%',
});

const headingStyles = xcss({
	alignItems: 'flex-start',
	textAlign: 'left',
	width: '100%',
});
